import React, { useState, useRef, useEffect } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import _find from 'lodash/find.js';
import _kebabCase from 'lodash/kebabCase.js';
import { formatDoubleDigit } from '_ui-core-js/utilities/formatDoubleDigit.js';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { BackgroundMedia } from '_ui-core-js/React/components/BackgroundMedia.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { VIEWS } from './StyleSelector.jsx';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities/fireGTMTrackingEvent.js';
import { randomNumber } from '_ui-core-js/utilities/randomNumber.js';

const colorCodeMap = {
  'cool-and-subtle' : {
    cool: ['2YH', '2LB'],
    elegant: [ '1L1', '209', '1F7'],
    relax: ['1L1', '209', '1F7'],
    emotion: ['2YH', '2LB']
  },
  'crisp-and-sleek' : {
    cool: ['2YM', '2YN'],
    elegant: ['085', '1H5'],
    relax: ['085', '1H5'],
    emotion: ['2YM', '2YN']
  },
  'vibrant-and-bold' : {
    cool: ['2TB', '2PQ'],
    elegant: ['3U5', '5A3'],
    relax: ['3U5', '5A3'],
    emotion: ['2TB', '2PQ']
  },
  'warm-and-rich' : {
    cool: ['2YF', '2VT'],
    elegant: ['4Y5', '8W7'],
    relax: ['4Y5', '8W7'],
    emotion: ['2YF', '2VT']
  },
};

export function ChoicesScreen(props) {

  const { block, state, rootElRef, data, user } = props;
  const [activeLevel, setActiveLevel] = useState(0);
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [viewTransitionOut, setViewTransitionOut] = useState(false);
  const answersObjRef = useRef({ keywords: [] });
  // activeSubCategoryOrAtmosphereRef - Category = "your-nature", "your-style". Subcategory = "dynamic, sophisticated". Atmosphere = "cool, relax etc"
  const activeSubCategoryOrAtmosphereRef = useRef(getActiveSubCategoryOrAtmosphere()); 
  const totalNoOfQuestionsRef = useRef(getTotalNoOfQuestions());
  const buttonRef = useRef();
  const activeLevelData = data.levels[activeLevel];
  const activeCategory = _kebabCase(activeLevelData.type);
  const activeQuestionsData = 
    activeSubCategoryOrAtmosphereRef.current ? 
    [...activeLevelData.questions[activeSubCategoryOrAtmosphereRef.current] || [], ...activeLevelData.questions[activeCategory] || []] : 
    activeLevelData.questions[activeCategory];
  const activeQuestionData = activeQuestionsData[activeQuestion];
  const activeLevelQuestionsLength = activeQuestionsData.length;

  // If only 2 questions extend array to 4 items in order to render placeholders to keep layout consistent
  if (activeQuestionData.options.length === 2) {
    activeQuestionData.options.splice(2, 0, undefined, undefined);
  }

  function getTotalNoOfQuestions() {
    let total = 0;

    data.levels.forEach(level => {
      const firstItemKey = Object.keys(level.questions)[0];
      total += level.questions[firstItemKey].length;
    })
    return total;
  };

  function getActiveSubCategoryOrAtmosphere(category) {
    const categoryAnswers = answersObjRef.current[category || activeCategory];
    return !categoryAnswers ? null : Object.keys(categoryAnswers).reduce((a, b) => categoryAnswers[a] > categoryAnswers[b] ? a : b);
  };

  function updateAnswersObject(option, cateogry, result) {
    if (option) {
      answersObjRef.current.keywords.push(option);
    }

    if (cateogry) {
      const { key, value } = cateogry;
      const categoryObj = answersObjRef.current[key];

      if (categoryObj && categoryObj[value]) {
          categoryObj[value] += 1;
      } 
      else {
        answersObjRef.current[key] = { ...categoryObj, ...{ [value] : 1 }};
      }
    }

    if (result) {
      answersObjRef.current[result.key] = result.value;
    }
  };

  function selectAnswer(optionObj) {
    const option = _find(optionObj.dataAttributes, { key: 'value' }).value;
    const category = _find(optionObj.dataAttributes, { id: 'category' });
    updateAnswersObject(option, category);
    setViewTransitionOut(true);
  };

  function goToNextQuestion() {
    const isLastQuestionOnCurrentLevel = activeQuestion === activeLevelQuestionsLength - 1;

    if (isLastQuestionOnCurrentLevel) {
      const isLastQuestionInQuiz = activeLevel === data.levels.length - 1;
      activeSubCategoryOrAtmosphereRef.current = getActiveSubCategoryOrAtmosphere();

      // set recommended atmosphere and going to results
      if (isLastQuestionInQuiz) { 
        // Atmosphere is calculated at end of level 2 (your-style) so we need to get it by key here as
        // the current activeCategory will be equal to the last level (3/4) key.
        const atmosphere = getActiveSubCategoryOrAtmosphere('your-style');
        const yourNature = getActiveSubCategoryOrAtmosphere('your-nature');
        const colors = colorCodeMap[getActiveSubCategoryOrAtmosphere('color')][atmosphere];
        state.setKeywords(answersObjRef.current.keywords);
        state.setYourNature(yourNature);
        state.setCarColor(colors[randomNumber(0, colors.length)]);
        state.setRecommendedAtmosphere(atmosphere);
        state.setActiveView(VIEWS.loading.name);
      } 
      // go to first question of next level
      else { 
        setActiveLevel(prevVal => prevVal + 1);  
        setActiveQuestion(0);   
      }
    } 
    // go to next question in current level
    else {
      setActiveQuestion(prevVal => prevVal + 1);
    } 

    setActivePage(prevVal => prevVal + 1);
    setViewTransitionOut(false);
  };

  useEffect(() => {
    buttonRef.current.focus();
  }, [activePage]);

  useEffect(() => {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxStyleSelector, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.workflowStep,
      label: activePage
    }, {
      workflow: {
        name: LBX_GTM_TRACKING_VALUES.componentName.lbxStyleSelector,
        step: activePage,
        conversion: 0,
        variant: user.userPreferences && user.userPreferences.personality ? user.userPreferences.personality : CORE_GTM_TRACKING_VALUES.label.general
      }
    });
  }, [activePage]);

  return (
    <div 
    ref={rootElRef}
    className={classNames({
      [`${block}__screen-container`]: true,
      [`${block}__screen-container--${state.activeView}`]: true,
      'utils__section-padding-small--uniform ': true,
      'dark-theme': true
    })}
    >
      <CssAnimation 
      enter={{ name: 'fade-in', duration: 1.5 }} 
      exit={{ name: 'fade-out', duration: 0.75 }} 
      triggerEnter={!viewTransitionOut} 
      triggerExit={viewTransitionOut}
      renderView={!!activeQuestionData.backgroundVideoUrl}
      >
        <div> 
          <BackgroundMedia 
          mediaType={activeQuestionData.mediaType} 
          mediaUrl={activeQuestionData.backgroundVideoUrl} 
          videoConfig={{ muted: state.audioMuted }} 
          overlay={true}
          />
        </div>
      </CssAnimation>
      <div className={
        classNames({
          [`${block}__content-container`]: true,
          [`${block}__content-container--${state.activeView}`]: true,
          'container-xxxl': true
        })}
      >
        <div className={`${block}__pagination h5`}>
          <CssAnimation 
          enter={{ name: 'slide-up-fade-in', duration: 1,  }} 
          exit={{ name: 'short-slide-up-out-fade-out', duration: 0.5, delay: 0.15 }} 
          triggerEnter={!viewTransitionOut} 
          triggerExit={viewTransitionOut}
          >
            <span className={`${block}__pagination-page`}>
              { formatDoubleDigit(activePage) }
            </span>
          </CssAnimation>
          <span className={`${block}__pagination-divider`}>|</span>
          <span className={`${block}__pagination-page`}>
            { formatDoubleDigit(totalNoOfQuestionsRef.current) }
          </span>
        </div>

        <CssAnimation 
        enter={{ name: 'fade-in', duration: 1 }} 
        exit={{ name: 'fade-out', duration: 0.5 }} 
        triggerEnter={!viewTransitionOut} 
        triggerExit={viewTransitionOut}
        >
          <h2 className={`${block}__question`}>
            { activeQuestionData.copy }
          </h2>
        </CssAnimation>
        <CssAnimation 
        enter={{ name: 'slide-up-fade-in', duration: 0.5, easing: 'ease-out' }} 
        exit={{ name: 'short-slide-down-out-fade-out', duration: 0.5, easing: 'ease-in' }} 
        triggerEnter={!viewTransitionOut} 
        triggerExit={viewTransitionOut}
        onExitEnd={goToNextQuestion}
        >
          <div className={`${block}__option-buttons-container`}>
          {
            activeQuestionData.options?.map((option, index) => (
              option ?
                <Button 
                {...( index === 0 && { ref: buttonRef }) }
                key={option.label}
                type='secondary' 
                label={option.label}
                disableTracking={true}
                onClickCallback={() => selectAnswer(option)} 
                /> 
                :
                <div key={index} className={`${block}__option-button-placeholder c-button__cta cmp-btn btn-secondary`}></div>
            ))
          }
          </div>
        </CssAnimation>
      </div>
    </div>
  )
};

ChoicesScreen.propTypes = {
  block: propTypes.string.isRequired,
  state: propTypes.object.isRequired,
  rootElRef: propTypes.object.isRequired,
  data: propTypes.object.isRequired
};